/*
 * Lemonade Training Oy — all rights reserved.
 * Movement Registry — Cloud Function callable wrappers.
 */

import { httpsCallable } from "firebase/functions";
import { firebaseFunctions } from "@lemonadetraining/web-shared";
import type {
  MovementRegistryEntry,
  MovementRegistryCategory,
  MovementMeasureType,
  RadarAxis,
  ScoringType,
  BenchmarkStandards,
} from "@lemonadetraining/contracts";

/**
 * The locked type from @lemonadetraining/contracts does not yet include the
 * server-controlled override and tombstone fields that the backend writes.
 * Until the next types release, extend it locally for UI consumption. These
 * fields are read-only on the client — the backend rejects any attempt to set
 * them via update payloads (see validateUpdatePayload in functions/).
 */
export type MovementRegistryEntryWithMeta = MovementRegistryEntry & {
  /** null for box-original movements; global movement id for overrides */
  overrideOfGlobalID: string | null;
  /** soft-delete tombstone; UI filters these out */
  deletedAt: unknown;
  /** uid of admin who deleted */
  deletedBy: string | null;
};

export type MovementWritePayload = {
  name: string;
  category: MovementRegistryCategory;
  radarAxis: RadarAxis;
  scoringType: ScoringType;
  measureType: MovementMeasureType;
  tags: string[];
  parentMovementID: string | null;
  parentCoefficient: number | null;
  standards?: BenchmarkStandards | null;
};

export type CreateGlobalResponse = {
  success: boolean;
  movementID: string;
};

export type UpdateGlobalResponse = {
  success: boolean;
  movementID: string;
  affectedMemberCount: number | null;
  affectedBoxOverrideCount: number;
};

export type DeleteGlobalResponse = {
  success: boolean;
  movementID: string;
  cascadedBoxOverrides: number;
  orphanedRecordCount: number;
  orphanedRecordCountIsCapped: boolean;
  cascadeError: string | null;
};

export type CreateBoxResponse = {
  success: boolean;
  movementID: string;
  boxID: string;
  overrideOfGlobalID: string | null;
};

export type UpdateBoxResponse = {
  success: boolean;
  movementID: string;
  boxID: string;
  affectedMemberCount: number;
};

export type DeleteBoxResponse = {
  success: boolean;
  movementID: string;
  boxID: string;
};

export type EffectiveRegistryResponse = {
  success: boolean;
  boxID: string | null;
  movements: MovementRegistryEntryWithMeta[];
  count: number;
};

const call = <Req, Res>(name: string) => {
  const fn = httpsCallable<Req, Res>(firebaseFunctions, name);
  return async (data: Req): Promise<Res> => {
    const result = await fn(data);
    return result.data;
  };
};

export const createGlobalMovement = call<MovementWritePayload, CreateGlobalResponse>(
  "createGlobalMovement"
);

export const updateGlobalMovement = call<
  Partial<MovementWritePayload> & { id: string },
  UpdateGlobalResponse
>("updateGlobalMovement");

export const deleteGlobalMovement = call<{ id: string }, DeleteGlobalResponse>(
  "deleteGlobalMovement"
);

export const createBoxMovement = call<
  MovementWritePayload & { boxID: string; overrideOfGlobalID?: string | null },
  CreateBoxResponse
>("createBoxMovement");

export const updateBoxMovement = call<
  Partial<MovementWritePayload> & { id: string; boxID: string },
  UpdateBoxResponse
>("updateBoxMovement");

export const deleteBoxMovement = call<{ id: string; boxID: string }, DeleteBoxResponse>(
  "deleteBoxMovement"
);

export const getEffectiveRegistry = call<{ boxID?: string | null }, EffectiveRegistryResponse>(
  "getEffectiveRegistry"
);
